// Updated version of WarehouseProcessing.js for reprinting processing orders
import React, { useState, useEffect, useRef } from 'react';
import {
    Breadcrumb, Layout, Space, Button, Table, Checkbox, message, Spin, Tag, Typography, Modal, List, Alert as AntAlert,
    Col,
    Row,
    Card,
    Input
} from 'antd';
import { ClearOutlined, SearchOutlined } from '@ant-design/icons';
import Highlighter from 'react-highlight-words';
import axios from 'axios';
import { pdf } from '@react-pdf/renderer';
import { MAINAPIURL, APPBACKGROUND_COLOR } from '../appsettings.js';
import Headerdiv from "../layout/header.js";
import { Link, useNavigate } from 'react-router-dom';
import Footerdiv from "../layout/footer.js";
import { AlertError, AlertInfo, AlertSuccess } from '../usableclasses.js';
import BulkOrderPrintDocument from './BulkOrderPrint.js';

const { Content } = Layout;

const InProcessing = () => {
    const { Text } = Typography;
    const ApiURL = MAINAPIURL;
    const TOKEN = localStorage.getItem("AUTHCODE");
    const UPDATED_BY = localStorage.getItem("USERID");
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [orders, setOrders] = useState([]);
    const [selectedOrderIds, setSelectedOrderIds] = useState([]);

    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isProcessingInModal, setIsProcessingInModal] = useState(false);
    const [processedOrdersInModal, setProcessedOrdersInModal] = useState([]);
    const [processingErrorInModal, setProcessingErrorInModal] = useState(null);
    const [tableFilters, setTableFilters] = useState({}); // State to hold table filters

    // State for table search
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');

    const fetchOrders = async () => {
        try {
            setLoading(true);
            const headers = { SystemAccessToken: TOKEN };
            const res = await axios.get(`${ApiURL}/warehouse/allprocessingorders`, { headers });
            setOrders(res.data || []);
            setSelectedOrderIds([]);
        } catch (error) {
            AlertError("Failed to fetch processing orders.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrders();
    }, []);

    const handleSelect = (orderId) => {
        if (!orderId) return;
        setSelectedOrderIds(prev =>
            prev.includes(orderId)
                ? prev.filter(id => id !== orderId)
                : prev.length < 10
                    ? [...prev, orderId]
                    : (message.warning("Max 10 orders allowed"), prev)
        );
    };

    const handleSelectTop = (count) => {
        if (count > 10) {
            message.warning("You can only select up to 10 orders for reprinting.");
        }
        const topOrderIds = orders
            .slice(0, count)
            .map(o => o.orderId)
            .filter(Boolean) // Filters out any null or undefined IDs
            .slice(0, 10); // Always cap at 10
        setSelectedOrderIds(topOrderIds);
    };

    const handleOpenProcessModal = () => {
        if (selectedOrderIds.length === 0) {
            AlertError("No orders selected.");
            return;
        }
        setIsModalVisible(true);
        setProcessedOrdersInModal([]);
        setProcessingErrorInModal(null);
        processOrdersInModal();
    };

    const processOrdersInModal = async () => {
        // Safeguard: Ensure no more than 10 orders are processed, though UI should prevent this.
        if (selectedOrderIds.length > 10) {
            const errorMsg = "Cannot reprint more than 10 orders at a time. Please select up to 10 orders.";
            AlertError(errorMsg);
            setProcessingErrorInModal(errorMsg); // Display error in the modal
            setIsProcessingInModal(false);      // Stop modal's own loading indicator
            return;                             // Prevent API call
        }

        setIsProcessingInModal(true);
        setProcessingErrorInModal(null); // Clear any previous error shown in modal
        try {
            const headers = { SystemAccessToken: TOKEN };
            const payload = {
                orderIds: selectedOrderIds.join(','),
                processRequestBy: UPDATED_BY
            };

            const res = await axios.post(`${ApiURL}/warehouse/bulksendtoreprint`, payload, { headers });
            const ordersFromServer = res.data || [];

            if (ordersFromServer.length === 0) {
                AlertInfo("No orders reprinted or no data returned.");
            } else {
                setProcessedOrdersInModal(ordersFromServer);
                AlertSuccess(`${ordersFromServer.length} order(s) reprinted successfully!`);
                fetchOrders();
                setSelectedOrderIds([]);
            }
        } catch (error) {
            const msg = error.response?.data?.message
                || error.message
                || "Unexpected error during reprint.";
            AlertError(msg);
            setProcessingErrorInModal(msg);
            setProcessedOrdersInModal([]);
        } finally {
            setIsProcessingInModal(false);
        }
    };

    const handlePrintInModal = async () => {
        if (!processedOrdersInModal.length) {
            AlertError("No processed orders available to print.");
            return;
        }
        try {
            const document = <BulkOrderPrintDocument ordersData={processedOrdersInModal} />;
            const pdfBlob = await pdf(document).toBlob();
            const pdfUrl = URL.createObjectURL(pdfBlob);
            const printWindow = window.open(pdfUrl, '_blank');

            if (printWindow) {
                printWindow.onload = () => {
                    printWindow.print();
                    URL.revokeObjectURL(pdfUrl);
                };
                printWindow.onclose = () => URL.revokeObjectURL(pdfUrl);
            } else {
                AlertError("Could not open print window. Check popup blocker.");
            }
        } catch (err) {
            AlertError(`Error generating PDF: ${err.message}`);
        }
    };

    const searchInput = useRef(null);

    const handleSearch = (selectedKeys, confirm, dataIndex, closeDropdown = true) => { // Added closeDropdown parameter
        confirm({ closeDropdown });
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
        setTableFilters(prev => ({
            ...prev,
            [dataIndex]: selectedKeys[0] ? [selectedKeys[0]] : null // Store as array for consistency with AntD filters
        }));
    };

    const getColumnSearchProps = (dataIndex, title) => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${title}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => {
                            clearFilters(); // Clears internal AntD filter state for this column
                            setSearchText(''); // Clear local search text state
                            setSearchedColumn(''); // Clear local searched column state
                            setTableFilters(prev => { // Update the global tableFilters state for this column
                                const newFilters = { ...prev };
                                delete newFilters[dataIndex];
                                return newFilters;
                            });
                            confirm({ closeDropdown: true }); // Close dropdown and apply changes
                        }}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button type="link" size="small" onClick={() => { close(); }}>
                        Close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        onFilter: (value, record) =>
            // Ensure dataIndex exists on record before calling toString()
            // This handles cases where a column might be derived or conditionally rendered
            // and the dataIndex might not be directly present on the record object.
            // Also, convert to string before checking includes.
            record[dataIndex]
                ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase())
                : '',
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }} searchWords={[searchText]} autoEscape textToHighlight={text ? text.toString() : ''} />
            ) : (text),
        filteredValue: tableFilters[dataIndex] || null, // Control filter value
    });

    const columns = [
        {
            title: 'Action',
            key: 'action',
            fixed: 'left', // Keep action column visible
            render: (_, record) => (
                <Checkbox
                    checked={selectedOrderIds.includes(record.orderId)}
                    onChange={() => handleSelect(record.orderId)}
                    disabled={!record.orderId}
                />
            ),
        },
        {
            title: 'Order No',
            dataIndex: 'orderRef',
            key: 'orderRef', // Added key
            ...getColumnSearchProps('orderRef', 'Order No')
        },
        {
            title: 'Date/Time',
            dataIndex: 'orderDateTime',
            key: 'orderDateTime', // Added key
            render: (text) => new Date(text).toLocaleString(),
            // No getColumnSearchProps for date fields
        },
        {
            title: 'Platform',
            dataIndex: 'orderPlatform',
            key: 'orderPlatform', // Added key
            render: (text) => text?.toUpperCase() || 'N/A',
            ...getColumnSearchProps('orderPlatform', 'Platform'),
        },
        {
            title: 'Order Total',
            dataIndex: 'orderCost',
            key: 'orderCost', // Added key
            ...getColumnSearchProps('orderCost', 'Order Total')
        },
        {
            title: 'Ship Method',
            dataIndex: 'shippedMethod',
            key: 'shippedMethod', // Added key
            ...getColumnSearchProps('shippedMethod', 'Ship Method')
        },
        {
            title: 'Is Paid',
            dataIndex: 'isPaid',
            key: 'isPaid', // Added key
            render: (isPaid) => <Tag color={isPaid ? 'green' : 'red'}>{isPaid ? 'Yes' : 'No'}</Tag>,
            // No getColumnSearchProps for boolean fields, AntD's built-in filters are better
        },
        {
            title: 'Is Cancel',
            dataIndex: 'isCanceled',
            key: 'isCanceled', // Added key
            render: (isCanceled) => <Tag color={isCanceled ? 'red' : 'green'}>{isCanceled ? 'Yes' : 'No'}</Tag>,
            // No getColumnSearchProps for boolean fields
        },
    ];

    // Handle table change event (pagination, filters, sorter)
    const handleTableChange = (pagination, filters, sorter) => {
        // Ant Design's Table onChange provides the current filters object
        // This is crucial for boolean filters (Is Paid, Is Cancel) which are not handled by getColumnSearchProps
        setTableFilters(filters);
        // If you have server-side pagination/sorting/filtering, you would trigger data fetch here
        // e.g., fetchOrders(filters, sorter, pagination);
    };

    const handleClearAllTableFilters = () => {
        setTableFilters({}); // Clear all filters
        setSearchText(''); // Clear any active search text
        setSearchedColumn(''); // Clear any active searched column
    };

    return (
        <Spin spinning={loading} tip="Loading...">
            <Layout style={{ backgroundColor: APPBACKGROUND_COLOR }}>
                <Headerdiv />
                <Content style={{ padding: '10px' }}>
                    <Breadcrumb style={{ margin: "16px 0" }}>
                        <Breadcrumb.Item>Warehouse</Breadcrumb.Item> {/* Keep the parent breadcrumb */}
                        <Breadcrumb.Item>In Processing </Breadcrumb.Item>
                    </Breadcrumb>

                    <Card style={{ marginBottom: 16 }}>
                        <Row gutter={[16, 16]} justify="space-between" align="middle">
                            <Col xs={24} sm={12} md={12}>
                                <Space wrap>
                                    <Tag color="blue">Total In Process: {orders.length}</Tag>
                                    <Tag color="green">Selected: {selectedOrderIds.length}</Tag>
                                    <Tag color="red">Max Selection: 10</Tag>
                                </Space>
                            </Col>
                            <Col xs={24} sm={12} md={12} style={{ textAlign: 'right' }}>
                                <Space wrap>
                                    <Button onClick={fetchOrders}>Refresh</Button>
                                    <Button onClick={() => handleSelectTop(10)}>Select Top 10</Button>
                                    <Button
                                        type="primary"
                                        disabled={selectedOrderIds.length === 0}
                                        onClick={handleOpenProcessModal}
                                    >
                                        Reprint Selected
                                    </Button>
                                    <Button
                                        icon={<ClearOutlined />}
                                        onClick={handleClearAllTableFilters}
                                        style={{ marginLeft: 8 }}
                                    >
                                        Clear All Table Filters
                                    </Button>
                                </Space>
                            </Col>
                        </Row>
                    </Card>

                    <Table
                        dataSource={orders}
                        columns={columns}
                        rowKey="orderId"
                        scroll={{ x: 'max-content' }}
                        pagination={false}
                        onChange={handleTableChange} // Add onChange handler
                        rowClassName={(record) =>
                            record.orderId && selectedOrderIds.includes(record.orderId) ? 'ant-table-row-selected' : ''
                        }
                        locale={{
                            emptyText: loading ? 'Fetching orders...' : 'No orders are currently in process.'
                        }}
                    />

                    <Modal
                        title="Reprint Confirmation"
                        open={isModalVisible}
                        onCancel={() => setIsModalVisible(false)}
                        footer={[
                            <Button key="close" onClick={() => setIsModalVisible(false)}>Close</Button>,
                            <Button
                                key="print"
                                type="primary"
                                onClick={handlePrintInModal}
                                disabled={isProcessingInModal || processedOrdersInModal.length === 0}
                                loading={isProcessingInModal && processedOrdersInModal.length > 0}
                            >
                                Print Orders
                            </Button>
                        ]}
                        width={600}
                    >
                        {isProcessingInModal && (
                            <Spin tip="Reprinting..." style={{ display: 'block', textAlign: 'center', marginBottom: 20 }} />
                        )}

                        {!isProcessingInModal && processingErrorInModal && (
                            <AntAlert
                                message="Reprint Error"
                                description={processingErrorInModal}
                                type="error"
                                showIcon
                                style={{ marginBottom: 16 }}
                            />
                        )}

                        {!isProcessingInModal && !processingErrorInModal && processedOrdersInModal.length > 0 && (
                            <>
                                <Typography.Title level={5}>Successfully Reprinted Orders:</Typography.Title>
                                <List
                                    bordered
                                    dataSource={processedOrdersInModal}
                                    renderItem={order => (
                                        <List.Item>
                                            <Tag color="green">{order.orderReferance || 'N/A'}</Tag>
                                        </List.Item>
                                    )}
                                />
                            </>
                        )}

                        {!isProcessingInModal && !processingErrorInModal && processedOrdersInModal.length === 0 && (
                            <Text>No orders were reprinted.</Text>
                        )}
                    </Modal>
                </Content>
                <Footerdiv />
            </Layout>
        </Spin>
    );
};

export default InProcessing;
